import React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Match } from 'effect';
import Icon from '../icon/Icon';

export type NoteProps = {
  children: React.ReactNode;
  className?: string;
} & NonNullableVariantProps;

type NonNullableVariantProps = {
  [K in keyof VariantProps<typeof noteVariants>]-?: NonNullable<VariantProps<typeof noteVariants>[K]>;
};

export const noteVariants = cva('flex flex-row items-center py-[8px] px-[12px] gap-[8px] text-venn-black', {
  variants: {
    variant: {
      info: 'bg-venn-switch-light',
      alert: 'bg-venn-highlight-pale',
      warning: 'bg-venn-warning',
      error: 'bg-venn-error-light', // todo
    },
    size: {
      small: 'text-[12px]',
      large: 'text-[14px]',
    },
  },
});

const Note = ({ children, variant, size, className }: NoteProps) => (
  <div className={cn(noteVariants({ variant, size }), className)}>
    <NoteIcon variant={variant} />
    {children}
  </div>
);

type IconProps = {
  variant: NonNullableVariantProps['variant'];
};

const NoteIcon = ({ variant }: IconProps) =>
  Match.value(variant).pipe(
    Match.when('info', () => <Icon type="square-info" prefix="fas" className="text-venn-dark-blue" />),
    Match.when('alert', () => <Icon type="circle-exclamation" prefix="far" className="text-venn-highlight-dark" />),
    Match.when('warning', () => <Icon type="diamond-exclamation" prefix="fas" className="text-venn-orange-dark" />),
    Match.when('error', () => <Icon type="triangle-exclamation" prefix="fas" className="text-venn-error" />),
    Match.exhaustive,
  );

export { Note };
